import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@groot/shell/lib/api";

/** Mirror of the server's `ShipmentTracking` row. */
export interface ShipmentTracking {
  id: number;
  orderId: number | null;
  trackingNumber: string;
  provider: string;
  state:
    | "pending"
    | "booked"
    | "in_transit"
    | "out_for_delivery"
    | "delivered"
    | "returned"
    | "exception"
    | "not_found";
  statusLabel: string | null;
  bookingDetails: TrackingBookingDetails | null;
  bookedAt: string | null;
  deliveredAt: string | null;
  lastEventAt: string | null;
  lastFetchAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface TrackingBookingDetails {
  articleType: string | null;
  bookedOn: string | null;
  bookedAtOffice: string | null;
  originPincode: string | null;
  originCity: string | null;
  destinationOffice: string | null;
  destinationPincode: string | null;
  destinationCity: string | null;
  weightGrams: number | null;
  tariff: number | null;
  codAmount: number | null;
  sourceCountry: string | null;
  destinationCountry: string | null;
}

export interface TrackingEvent {
  id: number;
  trackingId: number;
  eventCode: string;
  eventName: string;
  milestone: "booked" | "in_transit" | "out_for_delivery" | "delivered" | "returning" | "exception";
  office: string | null;
  officeId: string | null;
  remarks: string | null;
  eventAt: string;
  firstSeenAt: string;
}

export interface TrackingResponse {
  tracking: ShipmentTracking;
  events: TrackingEvent[];
  refreshError?: string;
  fetchedNow: boolean;
}

/** Human labels for the normalized tracking states. */
export const TRACKING_STATE_LABELS: Record<ShipmentTracking["state"], string> = {
  pending: "Pending",
  booked: "Booked",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  returned: "Returned to sender",
  exception: "Needs attention",
  not_found: "No updates yet",
};

/** "31 Oct, 4:59 pm" — compact en-IN formatting used across tracking UI. */
export function formatTrackingDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "Updated 5 min ago" style relative label. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export function useTracking(trackingNumber: string, orderId?: number) {
  const query = new URLSearchParams();
  if (orderId) query.set("orderId", String(orderId));
  const suffix = orderId ? `?${query.toString()}` : "";
  return useQuery({
    queryKey: ["tracking", trackingNumber, orderId ?? null],
    queryFn: async () => {
      return apiClient.get<TrackingResponse>(
        `/tracking/${encodeURIComponent(trackingNumber)}${suffix}`,
      );
    },
    enabled: !!trackingNumber,
    staleTime: 60_000,
  });
}

export function useRefreshTracking(trackingNumber: string, orderId?: number) {
  const queryClient = useQueryClient();
  const suffix = orderId ? `?orderId=${orderId}` : "";
  return useMutation({
    mutationFn: async () => {
      return apiClient.post<TrackingResponse>(
        `/tracking/${encodeURIComponent(trackingNumber)}/refresh${suffix}`,
      );
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["tracking", trackingNumber, orderId ?? null], data);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}
